export interface Plan {
  id: string;
  name: string;
  bestSuitedFor: string;
  keyBenefits: string[];
  specialNotes?: string;
  brochureUrl: string;
}

export interface InsuranceCompany {
  id: string;
  name: string;
  shortName: string;
  description: string;
  logoColor: string;
  logoIcon: string;
  officialBrochurePage: string;
  plans: Plan[];
}

export const healthInsuranceCompanies: InsuranceCompany[] = [
  {
    id: "hdfc-ergo",
    name: "HDFC ERGO Health Insurance",
    shortName: "HDFC ERGO",
    description:
      "One of India's leading health insurance providers with comprehensive coverage options and a wide network of 13,000+ cashless hospitals.",
    logoColor: "from-red-600 to-red-700",
    logoIcon: "ri-hospital-line",
    officialBrochurePage: "https://www.hdfcergo.com/download/brochure/health",
    plans: [
      {
        id: "hdfc-optima-secure",
        name: "Optima Secure",
        bestSuitedFor: "Individuals & families seeking comprehensive health coverage",
        keyBenefits: [
          "2X coverage from Day 1 with Secure Benefit",
          "No room rent capping",
          "Cashless treatment at 13,000+ hospitals",
          "Restoration of sum insured unlimited times",
          "Pre & post hospitalization coverage",
          "Daycare procedures covered",
          "Annual health check-up",
        ],
        brochureUrl:
          "https://www.hdfcergo.com/docs/default-source/downloads/brochures/optima-secure-brochure.pdf",
      },
      {
        id: "hdfc-optima-secure-plus",
        name: "Optima Secure Plus",
        bestSuitedFor: "High-net-worth individuals & families wanting top-tier coverage",
        keyBenefits: [
          "All Optima Secure benefits included",
          "Higher sum insured options up to ₹2 Crore",
          "International coverage for planned treatments",
          "OPD & dental coverage add-ons",
          "Maternity coverage with newborn baby cover",
          "Air ambulance coverage",
          "Personal accident cover included",
        ],
        brochureUrl:
          "https://customer-portal-assets.hdfcergo.com/documents/OptimaSecure%2B-893369221336.pdf",
      },
      {
        id: "hdfc-energy",
        name: "Energy Plan / Energy Diabetes Insurance Plan",
        bestSuitedFor: "Diabetes patients looking for specialized health coverage",
        keyBenefits: [
          "Designed exclusively for diabetes patients",
          "Coverage for diabetes-related complications",
          "Annual diabetes health check-ups",
          "OPD coverage for diabetes consultations",
          "No medical check-up required up to age 45",
          "Cashless hospitalization for diabetes complications",
          "Restoration benefit available",
        ],
        specialNotes:
          "Energy Plan is only for diabetes patients. Premiums and coverage are tailored for pre-existing diabetic conditions.",
        brochureUrl:
          "https://www.hdfcergo.com/docs/default-source/downloads/brochures/energy-brochure-online.pdf",
      },
    ],
  },
  {
    id: "niva-bupa",
    name: "Niva Bupa Health Insurance",
    shortName: "Niva Bupa",
    description:
      "A trusted name in Indian health insurance offering innovative products with unlimited recharge benefits and an expansive hospital network.",
    logoColor: "from-orange-500 to-orange-600",
    logoIcon: "ri-heart-pulse-line",
    officialBrochurePage: "https://www.nivabupa.com/health-insurance-plans.html",
    plans: [
      {
        id: "niva-reassure",
        name: "ReAssure",
        bestSuitedFor: "Young families & individuals wanting unlimited recharge",
        keyBenefits: [
          "Unlimited recharge of sum insured",
          "Cashless treatment at 10,000+ hospitals",
          "No room rent restrictions",
          "Pre & post hospitalization expenses covered",
          "Daycare procedures covered",
          "Domiciliary hospitalization",
          "Road ambulance coverage",
        ],
        brochureUrl:
          "https://www.nivabupa.com/content/dam/nivabupa/PDF/reassure-2-0/ReAssure%20-%20%20Brochure.pdf",
      },
      {
        id: "niva-reassure-2",
        name: "ReAssure 2.0",
        bestSuitedFor: "Families upgrading from ReAssure for better benefits",
        keyBenefits: [
          "Unlimited recharge of base sum insured",
          "No sub-limits on room rent or ICU",
          "Modern treatments covered (robotic surgery, etc.)",
          "Pre-hospitalization for 60 days & post for 180 days",
          "Annual health check-up for all insured members",
          "Alternative treatments (AYUSH) covered",
          "Second opinion for critical illnesses",
        ],
        brochureUrl:
          "https://www.nivabupa.com/content/dam/nivabupa/PDF/reassure-2-0/ReAssure%202.0%20-%20SS.pdf",
      },
      {
        id: "niva-reassure-3",
        name: "ReAssure 3.0",
        bestSuitedFor: "Customers seeking the most comprehensive unlimited coverage",
        keyBenefits: [
          "Lifetime renewability guarantee",
          "Unlimited sum insured recharge",
          "Coverage for advanced modern treatments",
          "Hospital cash benefit for every claim-free year",
          "Home treatment (domiciliary) coverage",
          "Organ donor expenses covered",
          "Emergency worldwide coverage",
        ],
        brochureUrl:
          "https://transactions.nivabupa.com/pages/doc/brochure/Reassure30_SS_Unlimited.pdf",
      },
      {
        id: "niva-aspire",
        name: "Aspire",
        bestSuitedFor: "Individuals & young professionals seeking affordable coverage",
        keyBenefits: [
          "Affordable premiums for young age groups",
          "Instant claim settlement for small amounts",
          "No room rent capping",
          "Daycare & domiciliary treatments covered",
          "Pre & post hospitalization coverage",
          "Cashless network hospital access",
          "Tax benefits under Section 80D",
        ],
        brochureUrl:
          "https://transactions.nivabupa.com/pages/doc/brochure/aspire_ss.pdf?v=1.0",
      },
      {
        id: "niva-health-premia",
        name: "Health Premia",
        bestSuitedFor: "Premium customers seeking luxury healthcare & global coverage",
        keyBenefits: [
          "Worldwide emergency hospitalization coverage",
          "Air ambulance & road ambulance included",
          "Second medical opinion globally",
          "Maternity & newborn baby cover",
          "OPD cover with dental & vision benefits",
          "Personal accident cover up to base SI",
          "Health check-up & wellness programs",
        ],
        brochureUrl:
          "https://www.nivabupa.com/content/dam/nivabupa/PDF/health-premia/healthpremia%20brochure.pdf",
      },
    ],
  },
  {
    id: "icici-lombard",
    name: "ICICI Lombard Health Insurance",
    shortName: "ICICI Lombard",
    description:
      "One of India's largest private general insurers known for quick claim settlement, digital-first services, and robust health coverage plans.",
    logoColor: "from-teal-600 to-teal-700",
    logoIcon: "ri-shield-star-line",
    officialBrochurePage: "https://www.icicilombard.com/health-insurance",
    plans: [
      {
        id: "icici-complete-health",
        name: "Complete Health Insurance",
        bestSuitedFor: "Individuals & families wanting all-round health protection",
        keyBenefits: [
          "Comprehensive hospitalization coverage",
          "Pre & post hospitalization expenses",
          "Daycare procedures & domiciliary treatment",
          "Cashless claim at 8,000+ network hospitals",
          "Sum insured restoration benefit",
          "Annual preventive health check-up",
          "Tax benefits under Section 80D",
        ],
        brochureUrl:
          "https://www.icicilombard.com/docs/default-source/downloads/complete-health-insurance-brochure.pdf",
      },
      {
        id: "icici-elevate",
        name: "Elevate",
        bestSuitedFor: "Customers wanting high sum insured with smart features",
        keyBenefits: [
          "Smart health features with wellness rewards",
          "High sum insured options up to ₹1 Crore",
          "No co-pay or room rent restrictions",
          "Coverage for modern treatment methods",
          "Instant digital policy issuance",
          "Health & wellness app integration",
          "Loyalty discounts on renewal",
        ],
        brochureUrl:
          "https://www.icicilombard.com/docs/default-source/default-document-library/elevate-brochure_final_4-6-24.pdf",
      },
      {
        id: "icici-maxprotect",
        name: "MaxProtect",
        bestSuitedFor: "Customers seeking maximum coverage with add-on flexibility",
        keyBenefits: [
          "Maximum coverage with flexible add-ons",
          "Critical illness rider available",
          "Personal accident cover option",
          "Hospital daily cash benefit",
          "Ambulance coverage up to ₹3,000 per hospitalization",
          "Consumables cover add-on",
          "No claim bonus up to 50%",
        ],
        brochureUrl:
          "https://www.icicilombard.com/docs/default-source/default-document-library/1220943867_maxprotect-brochure-18c0003ff45fd68ff8a0df0055f51d9c7.pdf",
      },
      {
        id: "icici-health-shield",
        name: "Health Shield",
        bestSuitedFor: "Budget-conscious buyers wanting essential health coverage",
        keyBenefits: [
          "Essential hospitalization coverage at affordable premium",
          "Pre & post hospitalization for 30 & 60 days",
          "Daycare procedures covered",
          "Cashless network hospital access",
          "Tax deduction under Section 80D",
          "Lifelong renewability",
          "Option to upgrade sum insured on renewal",
        ],
        brochureUrl:
          "https://www.icicilombard.com/docs/default-source/policy-wordings-product-brochure/complete-health-insurance-%28health-shield%29.pdf",
      },
    ],
  },
];

export interface ComparisonRow {
  company: string;
  plan: string;
  bestSuitedFor: string;
  keyBenefits: string[];
  specialNotes?: string;
  brochureUrl: string;
}

export const comparisonData: ComparisonRow[] = healthInsuranceCompanies.flatMap(
  (company) =>
    company.plans.map((plan) => ({
      company: company.name,
      plan: plan.name,
      bestSuitedFor: plan.bestSuitedFor,
      keyBenefits: plan.keyBenefits.slice(0, 4),
      specialNotes: plan.specialNotes,
      brochureUrl: plan.brochureUrl,
    }))
);